import { createReducer, on } from '@ngrx/store';

import * as Offline from '../actions/offline.actions';
import { DownloadStatus } from '../../models';
import { DeliveryAsset, TrackTypes } from '../../models/delivery-asset.model';
import { DeliveryOfflineStatus, OfflineDelivery } from '../../models/delivery.model';
import { HelperService } from 'app/core/services/helper.service';

export const offlineFeatureKey = 'offline';

export interface DeliveryStatusItem {
  deliveryKeyCode: string;
  deliveryStatus: DownloadStatus;
}

export interface OfflineState {
  deliveries: OfflineDelivery[];
  isOffline: boolean;
  wifiOnly: boolean;
  cacheEnabled: boolean;
  networkType: string;
  basePath: string;
  isDownloading: boolean;
}

export const initialState: OfflineState = {
  deliveries: [],
  isOffline: false,
  wifiOnly: false,
  cacheEnabled: true,
  networkType: '',
  basePath: '',
  isDownloading: false,
};

export const reducer = createReducer(
  initialState,
  on(Offline.saveOfflineDelivery, (state: OfflineState, { delivery }) => {
    const deliveries = state.deliveries.map((oDelivery: OfflineDelivery) =>
      HelperService.cloneOfflineDelivery(<OfflineDelivery>oDelivery),
    );
    const existDelivery = deliveries.find(
      (deliveryItem: OfflineDelivery) => deliveryItem.deliveryKeyCode === delivery.deliveryKeyCode,
    );

    if (!existDelivery) {
      const offlineDelivery = HelperService.cloneOfflineDelivery(<OfflineDelivery>delivery);
      offlineDelivery.offlineStatus = 'downloading';
      deliveries.push(offlineDelivery);
    }
    return { ...state, deliveries };
  }),
  on(Offline.saveAudioAssets, (state: OfflineState, { deliveryKeyCode }) => {
    const deliveries = state.deliveries.map((oDelivery: OfflineDelivery) => {
      const delivery = HelperService.cloneOfflineDelivery(oDelivery);
      if (delivery.deliveryKeyCode === deliveryKeyCode) {
        delivery.offlineStatus = 'downloading';
      }

      return delivery;
    });
    return {
      ...state,
      deliveries: deliveries || [],
    };
  }),
  on(Offline.refreshOfflineDeliverySuccess, (state: OfflineState, { deliveries }) => ({
    ...state,
    deliveries: deliveries || [],
  })),
  on(Offline.refreshDownloading, (state: OfflineState, { deliveryKeyCode }) => {
    const deliveries = state.deliveries.map((delivery: OfflineDelivery) => {
      const newDelivery = HelperService.cloneOfflineDelivery(delivery);
      let offlineStatus: DeliveryOfflineStatus = 'downloaded';

      if (newDelivery.deliveryKeyCode === deliveryKeyCode) {
        newDelivery.offlineAudioAssets = (newDelivery.offlineAudioAssets || []).map(
          (asset: DeliveryAsset) => {
            const newAsset = { ...asset };

            if (newAsset.offlineDownloadStatus !== 'COMPLETED') {
              offlineStatus = 'downloading';
              newAsset.offlineDownloadStatus = 'STARTED';
            }

            return newAsset;
          },
        );
      }
      newDelivery.offlineStatus = offlineStatus;

      return newDelivery;
    });

    return { ...state, deliveries };
  }),
  on(Offline.removeOfflineDelivery, (state: OfflineState, { deliveryKeyCode }) => {
    const deliveries = (state.deliveries || []).map((delivery: OfflineDelivery) =>
      HelperService.cloneOfflineDelivery(delivery),
    ); 
    const findIndex = deliveries.findIndex(
      (delivery: OfflineDelivery) => delivery.deliveryKeyCode === deliveryKeyCode,
    );

    if (findIndex !== -1) {
      deliveries.splice(findIndex, 1);
    }

    return { ...state, deliveries };
  }),
  on(
    Offline.cancelDownloading,
    Offline.saveOfflineDeliveryFail,
    Offline.getDeliveriesAudioAssetsFail,
    (state: OfflineState, { deliveryKeyCode }) => {
      const deliveries = state.deliveries.map((oDelivery: OfflineDelivery) => {
        const delivery = HelperService.cloneOfflineDelivery(oDelivery);
        if (delivery.deliveryKeyCode === deliveryKeyCode) {
          return cancelDownloadingDelivery(delivery);
        }
        return delivery;
      });

      return { ...state, deliveries };
    },
  ),
  on(Offline.stopDownloading, (state: OfflineState) => {
    let deliveries = state.deliveries.map((oDelivery: OfflineDelivery) => {
      const delivery = HelperService.cloneOfflineDelivery(oDelivery);
      return cancelDownloadingDelivery(delivery);
    });

    deliveries = (deliveries || []).filter(
      (delivery: OfflineDelivery) => delivery.offlineStatus === 'downloaded',
    );

    return { ...state, deliveries };
  }),
  on(Offline.refreshOfflineDeliveryAfterLogin, (state: OfflineState, { deliveries }) => {
    let newDeliveries = (deliveries || []).map((oDelivery: OfflineDelivery) => {
      const delivery = HelperService.cloneOfflineDelivery(oDelivery);
      return cancelDownloadingDelivery(delivery);
    });

    newDeliveries = (newDeliveries || []).filter(
      (delivery: OfflineDelivery) => delivery.offlineStatus === 'downloaded',
    );

    return { ...state, deliveries: newDeliveries };
  }),
  on(Offline.saveAudioAssets, (state: OfflineState, { assets, deliveryKeyCode }) => {
    const stateDeliveries = state.deliveries.map((oDelivery: OfflineDelivery) =>
      HelperService.cloneOfflineDelivery(oDelivery),
    );
    const deliveries = stateDeliveries.map((delivery: OfflineDelivery) => {
      if (deliveryKeyCode === delivery.deliveryKeyCode) {
        delivery.offlineAudioAssets = [...assets];
      }

      return delivery;
    });
    return { ...state, deliveries };
  }),
  on(
    Offline.updateOfflineAsset,
    Offline.writeToAudioFileSuccess,
    (state: OfflineState, { asset }) => {
      const { deliveryKeyCode } = asset;
      const { basePath } = state;
      const stateDeliveries = (state.deliveries || []).map((delivery: OfflineDelivery) =>
        HelperService.cloneOfflineDelivery(delivery),
      );
      const curAsset = getAssetWithoutBathPath(asset, basePath);
      const deliveries = stateDeliveries.map((delivery: OfflineDelivery) => {
        if (deliveryKeyCode === delivery.deliveryKeyCode) {
          delivery.offlineAudioAssets = getOfflineAudioAssets(
            delivery.offlineAudioAssets,
            curAsset,
          );
          delivery.offlineStatus = getDeliveryStatus(delivery.offlineAudioAssets);
          delivery.assetGroups = [
            { assetCategoryName: 'Audio', numberOfAssets: delivery.offlineAudioAssets.length },
          ];
        }

        delivery.deliveryImage = delivery.deliveryImage.replace(basePath, '');
        delivery.mobileDeliveryImage = (
          delivery.mobileDeliveryImage || delivery.deliveryImage
        ).replace(basePath, '');

        return delivery;
      });

      return { ...state, deliveries };
    },
  ),
  on(
    Offline.storeDeliveryImageSuccess,
    (state: OfflineState, { deliveryKeyCode, fieldName, localUrl }) => {
      const deliveries: OfflineDelivery[] = state.deliveries.map((oDelivery: OfflineDelivery) => {
        const delivery = HelperService.cloneOfflineDelivery(oDelivery);
        if (delivery.deliveryKeyCode === deliveryKeyCode) {
          delivery[fieldName] = localUrl;
        }
        return delivery;
      });

      return {
        ...state,
        deliveries,
      };
    },
  ),
  on(Offline.setOfflineState, (state: OfflineState, { isOffline }) => ({ ...state, isOffline })),
  on(Offline.setOfflineOptions, (state: OfflineState, { options }) => {
    const { wifiOnly } = options;
    const cacheEnabled = options.offlineCacheNotificationEnabled;

    return { ...state, wifiOnly, cacheEnabled };
  }),
  on(Offline.setNetworkType, (state: OfflineState, { networkType }) => ({ ...state, networkType })),
  on(Offline.clearAllCache, (state: OfflineState) => ({ ...state, deliveries: [], cacheSize: 0 })),
  on(Offline.setBasePathSuccess, (state: OfflineState, { basePath }) => ({ ...state, basePath })),
  on(Offline.updateDeliveryFromServerSuccess, (state: OfflineState, { delivery }) => {
    const deliveries = state.deliveries.map((oDelivery: OfflineDelivery) => {
      const offlineDelivery = HelperService.cloneOfflineDelivery(oDelivery);
      if (offlineDelivery.deliveryKeyCode === delivery.deliveryKeyCode) {
        offlineDelivery.favourite = delivery.favourite;
        offlineDelivery.openedDelivery = delivery.openedDelivery;
        offlineDelivery.expiryDate = delivery.expiryDate;
      }
      return offlineDelivery;
    });

    return { ...state, deliveries };
  }),
  on(Offline.updateOfflineDownloadingState, (state: OfflineState, { isDownloading }) => (
    { ...state, isDownloading }
  )),
);

export const getOfflineStatus = (state: OfflineState) => state.isOffline;

function getDeliveryStatus(assets) {
  if (assets && assets.length) {
    const statusCount = {
      COMPLETED: 0,
      DOWNLOADING: 0,
      DAMAGED: 0,
      STARTED: 0,
      DELETED: 0,
      CANCELED: 0,
    };
    let deliveryStatus: DeliveryOfflineStatus = 'readyToDownload';

    assets.forEach((asset: DeliveryAsset) => {
      const assetStatus = asset.offlineDownloadStatus || 'STARTED';
      statusCount[assetStatus] += 1;
    });
    if (statusCount.DOWNLOADING || (statusCount.COMPLETED && statusCount.STARTED)) {
      deliveryStatus = 'downloading';
    } else if (statusCount.COMPLETED) {
      deliveryStatus = 'downloaded';
    } else {
      deliveryStatus = 'readyToDownload';
      if (statusCount.DAMAGED) {
        console.error('ALL file are broken');
      }
    }

    return deliveryStatus;
  }

  return '';
}

function getOfflineAudioAssets(assets: DeliveryAsset[], asset: DeliveryAsset): DeliveryAsset[] {
  if (assets) {
    const assetExist = assets.find(
      (curAsset: DeliveryAsset) => curAsset.campaignTrackId === asset.campaignTrackId,
    );

    if (assetExist) {
      return assets.map((dAsset: DeliveryAsset) => (
        asset.campaignTrackId === dAsset.campaignTrackId ? asset : dAsset
      ));
    }

    assets.push(asset);
    return assets;
  }

  return [asset];
}

function cancelDownloadingDelivery(delivery) {
  const newDelivery = HelperService.cloneOfflineDelivery(delivery);
  let offlineStatus: DeliveryOfflineStatus = 'readyToDownload';
  newDelivery.offlineAudioAssets = (newDelivery.offlineAudioAssets || []).map(
    (asset: DeliveryAsset) => {
      const newAsset = { ...asset };

      if (newAsset.offlineDownloadStatus === 'COMPLETED') {
        offlineStatus = 'downloaded';
      } else if (
        !newAsset.offlineDownloadStatus ||
        newAsset.offlineDownloadStatus === 'DOWNLOADING' ||
        newAsset.offlineDownloadStatus === 'STARTED'
      ) {
        newAsset.offlineDownloadStatus = 'CANCELLED';
      }

      return newAsset;
    },
  );
  newDelivery.offlineStatus = offlineStatus;
  return newDelivery;
}

function getAssetWithoutBathPath(asset: DeliveryAsset, basePath) {
  const curAsset = { ...asset };
  curAsset.types = (curAsset.types || []).map((type: TrackTypes) => ({ ...type }));

  curAsset.types = (curAsset.types || []).map((type: TrackTypes) => {
    type.url = type.url.replace(basePath, '');
    return type;
  });

  return curAsset;
}
